import React from "react";
import { Box, CircularProgress, Tooltip, SvgIcon } from "@mui/joy";
import { useEquipment } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import type {
  EquipmentAttributeDefinition,
  EquipmentModel,
} from "../../api/equipment/Equipment.typed";
import { extractDataFromResponse } from "../../utils/paginationHelpers";
import { SCreate, SearchRoundedIcon as SearchRounded } from "../../styles/StyledMUI";
import IconButtonCTA from "../common/IconButtonCTA";
import EquipmentModelsTable from "./display/EquipmentModelsTable";
import EquipmentModelModal from "./display/EquipmentModelModal";
import EquipmentModelOptionsModal from "./display/EquipmentModelOptionsModal";
import EquipmentModelAlertModal from "./display/EquipmentModelAlertModal";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import DarkInput from "../common/DarkInput";
import { colorTokens } from "../../styles/globalStyles";

export default function EquipmentModelsComponent() {
  const { hasAnyPermission } = usePermissions();
  const canManage = hasAnyPermission([Permission.EQUIPMENT_EDIT, Permission.EQUIPMENT_MODELS_EDIT]);
  const { getEquipmentModels, createEquipmentModel, updateEquipmentModel, deleteEquipmentModel } =
    useEquipment();
  const { showAlert } = useAlert();

  const [models, setModels] = React.useState<EquipmentModel[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Modal states
  const [open, setOpen] = React.useState(false);
  const [current, setCurrent] = React.useState<EquipmentModel | null>(null);
  const [optionIdx, setOptionIdx] = React.useState<number | null>(null);
  const [openOptionModal, setOpenOptionModal] = React.useState(false);
  const [alertIdx, setAlertIdx] = React.useState<number | null>(null);
  const [openAlertModal, setOpenAlertModal] = React.useState(false);

  const loadModels = React.useCallback(async () => {
    setLoading(true);
    const data = await getEquipmentModels();
    if (data) setModels(extractDataFromResponse(data));
    setLoading(false);
  }, [getEquipmentModels]);

  React.useEffect(() => {
    loadModels();
  }, [loadModels]);

  const openCreateModal = () => {
    if (!canManage) {
      showAlert("Vous n'avez pas les droits pour créer un modèle", "danger");
      return;
    }
    const model: EquipmentModel = {
      Name: "",
      Description: "",
      Attributes: [],
      Prefilters: [],
    } as EquipmentModel;
    setCurrent(model);
    setOpen(true);
  };

  const ensureIds = (attrs: EquipmentAttributeDefinition[]) =>
    attrs.map((a) => (a.ID ? a : { ...a, ID: crypto.randomUUID() }));

  const handleEditModel = (m: EquipmentModel) => {
    setCurrent({
      ...m,
      Attributes: ensureIds(m.Attributes || []),
      Prefilters: m.Prefilters || [],
    } as EquipmentModel);
    setOpen(true);
  };

  const saveModel = async () => {
    if (!current) return;
    if (!canManage) {
      showAlert("Vous n'avez pas les droits pour enregistrer ce modèle", "danger");
      return;
    }
    if (current.ID) {
      await updateEquipmentModel(current);
    } else {
      await createEquipmentModel(current);
    }
    setOpen(false);
    loadModels();
  };

  const handleDeleteModel = async (id?: number) => {
    if (!id) return;
    if (!canManage) {
      showAlert("Vous n'avez pas les droits pour supprimer ce modèle", "danger");
      return;
    }
    await deleteEquipmentModel(id);
    loadModels();
  };

  const filtered = React.useMemo(() => {
    return models.filter((m) => m.Name?.toLowerCase().includes(query.toLowerCase()));
  }, [models, query]);

  const openOptions = (idx: number) => {
    setOptionIdx(idx);
    setOpenOptionModal(true);
  };

  const openAlert = (idx: number) => {
    setAlertIdx(idx);
    setOpenAlertModal(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setCurrent(null);
  };

  const handleCloseOptionModal = () => {
    setOpenOptionModal(false);
    setOptionIdx(null);
  };

  const handleCloseAlertModal = () => {
    setOpenAlertModal(false);
    setAlertIdx(null);
  };

  return (
    <Box sx={{ width: "100%", p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <SvgIcon component={SearchRounded} sx={{ color: colorTokens.text.muted, fontSize: 20 }} />
        <DarkInput
          placeholder="Rechercher..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 180 }}
        />
        {canManage && (
          <Tooltip title="Nouveau modèle">
            <IconButtonCTA
              ctaVariant="accent"
              aria-label="Nouveau modèle"
              onClick={openCreateModal}
            >
              <SCreate />
            </IconButtonCTA>
          </Tooltip>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <EquipmentModelsTable
          models={filtered}
          onEditModel={handleEditModel}
          onDeleteModel={handleDeleteModel}
        />
      )}

      {/* Modals */}
      <EquipmentModelModal
        open={open}
        onClose={handleCloseModal}
        model={current}
        onModelChange={setCurrent}
        onSave={saveModel}
        onOpenOptions={openOptions}
        onOpenAlert={openAlert}
      />

      <EquipmentModelOptionsModal
        open={openOptionModal}
        onClose={handleCloseOptionModal}
        model={current}
        optionIdx={optionIdx}
        onModelChange={setCurrent}
      />

      <EquipmentModelAlertModal
        open={openAlertModal}
        onClose={handleCloseAlertModal}
        model={current}
        alertIdx={alertIdx}
        onModelChange={setCurrent}
      />
    </Box>
  );
}
